import * as React from "react";

import { ITheme, mergeStyleSets } from "@fluentui/react";

import {
  AppPersonaButton,
  getAppCompoundButtonStylesInMenu,
  getSpinButtonStylesInMenu,
} from "@1js/acui-button";
import { getCheckBoxStylesInMenu } from "@1js/acui-checkbox";
import { getComboBoxStylesInMenu } from "@1js/acui-combobox";
import { fontPickerLabelInMenuStyles } from "@1js/acui-font-picker";
import { AppIcons } from "@1js/acui-icons";
import { AppInput } from "@1js/acui-input";
import { deriveMenuKeytip } from "@1js/acui-menu";
import { AppSearchBox } from "@1js/acui-search-box";
import { AppThemeConsumer } from "@1js/acui-theme";
import {
  AppToggle,
  getToggleInMenuStyles,
  getToggleInMenuStylesMerged,
} from "@1js/acui-toggle";
import { AppTooltipManager } from "@1js/acui-tooltip";

import { RibbonControlGroup } from ".";
import {
  getGalleryPropsWithMenuDismiss,
  getPropsWithMenuDismiss,
} from "../controls/getPropsWithMenuDismiss";
import { MenuItemControlProps } from "../controls/Menu";
import {
  RibbonChangeColorsFullGallery,
  RibbonCharacterCodeGallery,
  RibbonCheckbox,
  RibbonColorPicker,
  RibbonComboBox,
  RibbonCompoundButton,
  RibbonFontPicker,
  RibbonFontStylesFullGallery,
  RibbonIconFullGallery,
  RibbonInkThicknessFullGallery,
  RibbonInsertTable,
  RibbonListGallery,
  RibbonPageNumberGallery,
  RibbonSlider,
  RibbonSpinButton,
  RibbonTableStylesFullGallery,
} from "../Ribbon/Controls";

function assertAppIconsExist(
  appIcons: AppIcons | undefined | null,
  controlType?: string
): appIcons is AppIcons {
  if (!appIcons) {
    throw new Error(
      `Missing appIcons for the control of type ${controlType} in a Ribbon menu`
    );
  }
  return true;
}

export function getReactComponentForRibbonControlInMenu(
  props: MenuItemControlProps,
  dismissMenu: () => void,
  itemIndex: number,
  totalSize: number,
  appIcons?: AppIcons
): JSX.Element {
  const positionInSet = isNaN(itemIndex) ? undefined : itemIndex;
  const setSize = isNaN(totalSize) ? undefined : totalSize;

  switch (props.type) {
    case "AppSpinButtonProps": {
      if (assertAppIconsExist(appIcons, props.type)) {
        return (
          <RibbonSpinButton
            positionInSet={positionInSet}
            setSize={setSize}
            {...props}
            styles={getSpinButtonStylesInMenu(props.styles)}
            incrementButtonIcon={appIcons.spinButtonUpChevron}
            decrementButtonIcon={appIcons.spinButtonDownChevron}
          />
        );
      }
      break;
    }
    case "AppComboBoxProps": {
      if (assertAppIconsExist(appIcons, props.type)) {
        return (
          <RibbonComboBox
            positionInSet={positionInSet}
            setSize={setSize}
            {...props}
            showLabel={true}
            styles={getComboBoxStylesInMenu(props.icon, props.styles)}
            pinToPreviousComboBox={false}
            downChevronIcon={appIcons.downChevron}
            identifiersAbove={false}
          />
        );
      }
      break;
    }
    case "AppCheckboxProps": {
      if (assertAppIconsExist(appIcons, props.type)) {
        return (
          <RibbonCheckbox
            positionInSet={positionInSet}
            styles={getCheckBoxStylesInMenu()}
            setSize={setSize}
            isMenuItem={true}
            {...props}
            checkmarkIcon={appIcons.checkboxCheckmark}
          />
        );
      }
      break;
    }
    case "AppColorPickerProps": {
      return (
        <RibbonColorPicker
          positionInSet={positionInSet}
          setSize={setSize}
          {...getPropsWithMenuDismiss(props, dismissMenu)}
          shouldTakeFocus={
            props.shouldTakeFocus !== undefined ? props.shouldTakeFocus : true
          }
        />
      );
    }
    case "AppFontPickerProps": {
      if (assertAppIconsExist(appIcons, props.type)) {
        const labelStyles = mergeStyleSets(
          fontPickerLabelInMenuStyles(!!props.icon),
          props.labelStyles
        );

        return (
          <RibbonFontPicker
            {...props}
            labelStyles={labelStyles}
            showLabel={true}
            downChevronIcon={appIcons.downChevron}
          />
        );
      }
      break;
    }
    case "AppToggleProps":
      return (
        <AppToggle
          {...props}
          styles={
            props.styles
              ? getToggleInMenuStylesMerged(props.styles)
              : getToggleInMenuStyles()
          }
        />
      );
    case "RibbonInsertTableProps": {
      return (
        <RibbonInsertTable
          positionInSet={positionInSet}
          setSize={setSize}
          appColors={props.appColors}
          {...(getPropsWithMenuDismiss(props, dismissMenu) as any)}
        />
      );
    }
    case "RibbonChangeColorsFullGalleryProps": {
      return (
        <RibbonChangeColorsFullGallery
          positionInSet={positionInSet}
          setSize={setSize}
          {...getGalleryPropsWithMenuDismiss(props, dismissMenu)}
        />
      );
    }
    case "AppCharacterCodeGalleryProps": {
      return (
        <RibbonCharacterCodeGallery
          positionInSet={positionInSet}
          setSize={setSize}
          {...getGalleryPropsWithMenuDismiss(props, dismissMenu)}
        />
      );
    }
    case "RibbonTableStylesFullGalleryProps": {
      return (
        <RibbonTableStylesFullGallery
          positionInSet={positionInSet}
          setSize={setSize}
          {...getGalleryPropsWithMenuDismiss(props, dismissMenu)}
        />
      );
    }
    case "AppListGalleryProps": {
      return (
        <RibbonListGallery
          positionInSet={positionInSet}
          setSize={setSize}
          {...getGalleryPropsWithMenuDismiss(props, dismissMenu)}
        />
      );
    }
    case "RibbonPageNumberFullGalleryProps": {
      return (
        <RibbonPageNumberGallery
          positionInSet={positionInSet}
          setSize={setSize}
          {...getGalleryPropsWithMenuDismiss(props, dismissMenu)}
        />
      );
    }
    case "AppIconFullGalleryProps": {
      return (
        <RibbonIconFullGallery
          positionInSet={positionInSet}
          setSize={setSize}
          {...getGalleryPropsWithMenuDismiss(props, dismissMenu)}
        />
      );
    }
    case "AppFontStylesFullGalleryProps": {
      return (
        <RibbonFontStylesFullGallery
          positionInSet={positionInSet}
          setSize={setSize}
          {...getGalleryPropsWithMenuDismiss(props, dismissMenu)}
        />
      );
    }
    case "AppInkThicknessFullGalleryProps": {
      return (
        <RibbonInkThicknessFullGallery
          positionInSet={positionInSet}
          setSize={setSize}
          {...getGalleryPropsWithMenuDismiss(props, dismissMenu)}
        />
      );
    }
    case "AppPersonaButtonProps":
      return (
        <AppPersonaButton
          positionInSet={positionInSet}
          setSize={setSize}
          {...props}
        />
      );
    case "AppCompoundButtonProps": {
      return (
        <AppThemeConsumer>
          {(theme: ITheme) => (
            <RibbonCompoundButton
              positionInSet={positionInSet}
              setSize={setSize}
              {...getPropsWithMenuDismiss(props, dismissMenu)}
              keytipProps={deriveMenuKeytip(props.keytipProps)}
              shouldTakeFocus={
                props.shouldTakeFocus !== undefined
                  ? props.shouldTakeFocus
                  : true
              }
              lang={props.lang}
              enableFocusOnHover={
                false
              } /* hovering the Ribbon menu items should not retain focus on mouse leave */
              styles={getAppCompoundButtonStylesInMenu(theme, props.styles)}
            />
          )}
        </AppThemeConsumer>
      );
    }
    case "AppSliderProps": {
      return (
        <RibbonSlider
          positionInSet={positionInSet}
          setSize={setSize}
          {...props}
          shouldTakeFocus={
            props.shouldTakeFocus !== undefined ? props.shouldTakeFocus : true
          }
        />
      );
    }
    case "AppInputProps": {
      return (
        <AppInput
          positionInSet={positionInSet}
          setSize={setSize}
          {...props}
          role="menuitem"
        />
      );
    }
    case "Multiline": {
      return (
        <AppTooltipManager>
          <RibbonControlGroup {...props} dismissMenu={dismissMenu} />
        </AppTooltipManager>
      );
    }
    case "AppSearchBoxProps": {
      return <AppSearchBox {...props} />;
    }
    default: {
      throw new Error(
        `The control of type ${props.type} is not valid in a Ribbon menu`
      );
    }
  }
  throw new Error("No JSX element returned");
}
